import { encode as encodeBase64 } from 'base-64';
import { secondsToMilliseconds } from 'date-fns';

import { FIVE_MINUTES, ONE_SECOND, THIRTY_SECONDS } from '@/constants/time';

/**
 * A subscribe-only client for the realtime Event API (AppSync Events), for web
 * and React Native.
 *
 * Ported from primum-infra's realtime package, which is private to that repo.
 * Keep the protocol handling in step with the original, ported at primum-infra
 * commit d7453ae46. See
 * [client.ts](https://github.com/primuminc/primum-infra/blob/main/packages/realtime/src/client.ts).
 * Local differences: `onDisconnected`, so a caller can tell a live
 * subscription from a dropped one between the drop and the reconnect; a guard
 * around frames that are not JSON, which on native would otherwise surface as
 * an uncaught error from the socket callback; and a base64url encoder that
 * does not depend on Hermes globals.
 *
 * The channel keeps no history, so a subscriber that was disconnected has
 * missed whatever was published meanwhile. `onSubscribed` fires with
 * `{ resumed: true }` after every reconnect precisely so a caller can re-read
 * its snapshot from the API; that read, not the socket, is the source of truth.
 */

const PROTOCOL = 'aws-appsync-event-ws';

const INITIAL_BACKOFF_MS = ONE_SECOND;

const MAX_BACKOFF_MS = THIRTY_SECONDS;

const DEFAULT_KEEPALIVE_TIMEOUT_MS = FIVE_MINUTES;

/** How long a new socket may take to reach `connection_ack` before it is abandoned. */
const CONNECT_TIMEOUT_MS = secondsToMilliseconds(15);

const UNAUTHORIZED_ERROR_TYPES = new Set(['Unauthorized', 'UnauthorizedException']);

const SOCKET_OPEN = 1;

/**
 * Only an authorization refusal is permanent: retrying with the same identity
 * gets the same answer. Throttling, internal errors and a token that expired
 * between connect and subscribe all clear on a reconnect with a fresh token.
 */
function isUnauthorized(errors: unknown): boolean {
  return (
    Array.isArray(errors) &&
    errors.some(
      (error) =>
        typeof error === 'object' &&
        error !== null &&
        UNAUTHORIZED_ERROR_TYPES.has((error as { errorType?: string }).errorType ?? '')
    )
  );
}

export type SubscribeOptions<T> = {
  /** The Event API's HTTP host, or its custom domain. No scheme, no path. */
  host: string;
  channel: string;
  /** Called on every connect and reconnect, so an expired token is refreshed. */
  getToken: () => Promise<string>;
  onEvent: (event: T) => void;
  /** Validates each event; an event that throws is reported and dropped. */
  parse?: (raw: unknown) => T;
  onSubscribed?: (info: { resumed: boolean }) => void;
  /** The socket closed while subscribed or connecting; a reconnect follows unless stopped. */
  onDisconnected?: () => void;
  /**
   * An unauthorized subscription (`fatal: true`) stops the client; anything
   * else is reported and followed by a reconnect.
   */
  onError?: (error: Error, info: { fatal: boolean }) => void;
  WebSocketImpl?: typeof WebSocket;
};

export type Unsubscribe = () => void;

type ServerMessage = {
  type: string;
  id?: string;
  event?: string;
  connectionTimeoutMs?: number;
  errors?: unknown;
};

/**
 * UTF-8 then base64url, without `TextEncoder` or `btoa`: the app already avoids
 * relying on either under Hermes, so this uses the same `base-64` package the
 * session code decodes tokens with.
 */
function base64Url(value: string): string {
  const binary = encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_match, hex: string) =>
    String.fromCharCode(parseInt(hex, 16))
  );

  return encodeBase64(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function parseServerMessage(data: unknown): ServerMessage | null {
  try {
    const parsed: unknown = JSON.parse(String(data));

    return typeof parsed === 'object' && parsed !== null && 'type' in parsed
      ? (parsed as ServerMessage)
      : null;
  } catch {
    return null;
  }
}

export function subscribe<T = unknown>(options: SubscribeOptions<T>): Unsubscribe {
  const Socket = options.WebSocketImpl ?? WebSocket;

  const subscriptionId = 'sub-1';

  let socket: WebSocket | undefined;

  let stopped = false;

  let everSubscribed = false;

  let backoffMs = INITIAL_BACKOFF_MS;

  let keepaliveTimeoutMs = DEFAULT_KEEPALIVE_TIMEOUT_MS;

  let keepaliveTimer: ReturnType<typeof setTimeout> | undefined;

  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  const report = (error: Error, fatal: boolean) => options.onError?.(error, { fatal });

  function armKeepalive(timeoutMs = keepaliveTimeoutMs) {
    clearTimeout(keepaliveTimer);

    keepaliveTimer = setTimeout(() => {
      report(new Error('Realtime connection went quiet'), false);

      socket?.close();
    }, timeoutMs);
  }

  function scheduleReconnect() {
    clearTimeout(keepaliveTimer);

    if (stopped) {
      return;
    }

    const delay = backoffMs;

    backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);

    reconnectTimer = setTimeout(() => {
      connect().catch((error: Error) => report(error, false));
    }, delay);
  }

  async function connect() {
    if (stopped) {
      return;
    }

    let token: string;

    try {
      token = await options.getToken();
    } catch (error) {
      report(error as Error, false);

      scheduleReconnect();

      return;
    }

    if (stopped) {
      return;
    }

    const authorization = { host: options.host, Authorization: token };

    let ws: WebSocket;

    try {
      ws = new Socket(`wss://${options.host}/event/realtime`, [
        PROTOCOL,
        `header-${base64Url(JSON.stringify(authorization))}`,
      ]);
    } catch (error) {
      report(error as Error, false);

      scheduleReconnect();

      return;
    }

    socket = ws;

    armKeepalive(CONNECT_TIMEOUT_MS);

    ws.onopen = () => ws.send(JSON.stringify({ type: 'connection_init' }));

    ws.onclose = () => {
      if (socket === ws) {
        if (!stopped) {
          options.onDisconnected?.();
        }

        scheduleReconnect();
      }
    };

    ws.onmessage = (message) => {
      armKeepalive();

      const msg = parseServerMessage(message.data);

      if (!msg) {
        return;
      }

      switch (msg.type) {
        case 'connection_ack':
          if (msg.connectionTimeoutMs) {
            keepaliveTimeoutMs = msg.connectionTimeoutMs;
          }

          armKeepalive();

          ws.send(
            JSON.stringify({
              type: 'subscribe',
              id: subscriptionId,
              channel: options.channel,
              authorization,
            })
          );

          return;

        case 'subscribe_success':
          backoffMs = INITIAL_BACKOFF_MS;

          options.onSubscribed?.({ resumed: everSubscribed });

          everSubscribed = true;

          return;

        case 'subscribe_error': {
          const fatal = isUnauthorized(msg.errors);

          if (fatal) {
            stopped = true;
          }

          report(
            new Error(`Subscription to ${options.channel} refused: ${JSON.stringify(msg.errors)}`),
            fatal
          );

          ws.close();

          return;
        }

        case 'data':
          if (msg.id !== subscriptionId || msg.event === undefined) {
            return;
          }

          try {
            const raw: unknown = JSON.parse(msg.event);

            options.onEvent(options.parse ? options.parse(raw) : (raw as T));
          } catch (error) {
            report(error as Error, false);
          }

          return;

        case 'connection_error':
        case 'error':
          report(new Error(`Realtime error: ${JSON.stringify(msg)}`), false);

          ws.close();

          break;

        default:
          break;
      }
    };
  }

  connect().catch((error: Error) => report(error, false));

  return () => {
    stopped = true;

    clearTimeout(keepaliveTimer);

    clearTimeout(reconnectTimer);

    const ws = socket;

    socket = undefined;

    if (ws && ws.readyState === SOCKET_OPEN) {
      ws.send(JSON.stringify({ type: 'unsubscribe', id: subscriptionId }));
    }

    ws?.close();
  };
}
